import React, { useEffect, useState } from "react";
import { Languages, Loader2 } from "lucide-react";
import { useTranslation } from "react-i18next";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

export const SUPPORTED_LANGUAGES = [
    { code: "en", labelKey: "languages.en" },
    { code: "tl", labelKey: "languages.tl" },
];

export default function LanguageSelector({ persistForUser = true, compact = false, className = "", testId = "language-selector" }) {
    const { t, i18n } = useTranslation();
    const auth = useAuth();
    const [value, setValue] = useState(i18n.resolvedLanguage || i18n.language || "en");
    const [saving, setSaving] = useState(false);

    useEffect(() => {
        const sync = (language) => {
            const next = SUPPORTED_LANGUAGES.some((item) => item.code === language) ? language : "en";
            setValue(next);
            document.documentElement.lang = next;
        };
        i18n.on("languageChanged", sync);
        sync(i18n.resolvedLanguage || i18n.language);
        return () => i18n.off("languageChanged", sync);
    }, [i18n]);

    const changeLanguage = async (next) => {
        if (!SUPPORTED_LANGUAGES.some((item) => item.code === next)) return;
        setValue(next);
        document.documentElement.lang = next;
        try { localStorage.setItem("aj-farm-lang", next); } catch { /* storage may be unavailable */ }
        await i18n.changeLanguage(next);
        if (!persistForUser || !auth?.user) return;
        setSaving(true);
        try {
            await api.put("/auth/language", { language: next });
            auth.updateUser?.({ language: next });
        } finally {
            setSaving(false);
        }
    };

    return (
        <label className={`flex items-center gap-2 ${className}`} data-testid="language-setting">
            {!compact && <Languages className="w-4 h-4 shrink-0 text-primary" aria-hidden="true" />}
            {!compact && <span className="text-sm font-medium">{t("common.language")}</span>}
            <select
                value={value}
                onChange={(event) => changeLanguage(event.target.value)}
                className="h-10 min-w-0 rounded-md border border-input bg-background px-3 text-sm text-foreground"
                aria-label={t("common.language")}
                data-testid={testId}
                disabled={saving}
            >
                {SUPPORTED_LANGUAGES.map((item) => (
                    <option key={item.code} value={item.code}>{t(item.labelKey)}</option>
                ))}
            </select>
            {saving && <Loader2 className="w-4 h-4 animate-spin" aria-label={t("common.saving")} />}
        </label>
    );
}
